<template>
  <div class="pointage-view">
    <Header title="Gestion des Pointages" />
    
    <!-- Stats Cards Row -->
    <div class="stats-cards">
      <div class="stat-card">
        <span class="material-icons">calendar_today</span>
        <div class="stat-content">
          <h3>{{ pointageStore.stats.totalToday }}</h3>
          <p>Pointages aujourd'hui</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-icons">check_circle</span>
        <div class="stat-content">
          <h3>{{ pointageStore.stats.totalPresent }}</h3>
          <p>Présents</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-icons">cancel</span>
        <div class="stat-content">
          <h3>{{ pointageStore.stats.totalAbsent }}</h3>
          <p>Absents</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-icons">schedule</span>
        <div class="stat-content">
          <h3>{{ pointageStore.stats.totalLate }}</h3>
          <p>Retards</p>
        </div>
      </div>
    </div>

    <!-- Control Panel -->
    <div class="control-panel">
      <div class="filters">
        <div class="date-picker">
          <label for="date-filter">Date:</label>
          <input 
            type="date" 
            id="date-filter" 
            v-model="selectedDate" 
            @change="handleDateChange"
          />
        </div>
        
        <div class="filter-dropdown">
          <label for="motif-filter">Motif:</label>
          <select 
            id="motif-filter" 
            v-model="selectedMotif"
            @change="handleMotifChange"
          >
            <option v-for="motif in motifs" :key="motif" :value="motif">
              {{ motif }}
            </option>
          </select>
        </div>
        
        <div class="filter-dropdown">
          <label for="direction-filter">Direction:</label>
          <select 
            id="direction-filter" 
            v-model="selectedDirection"
            @change="handleDirectionChange"
          >
            <option v-for="direction in directions" :key="direction" :value="direction">
              {{ direction }}
            </option>
          </select>
        </div>
        
        <div class="search-box">
          <input 
            type="text" 
            v-model="searchQuery" 
            @input="handleSearch" 
            placeholder="Rechercher un agent..."
          />
          <span class="material-icons">search</span>
        </div>
      </div>
      
      <div class="actions">
        <button class="add-btn" @click="showAddPointageModal = true">
          <span class="material-icons">add_circle</span>
          <span>Nouveau pointage</span>
        </button>
        <button 
          class="export-btn" 
          :disabled="pointageStore.filteredPointages.length === 0"
          @click="exportToExcel"
        >
          <span class="material-icons">file_download</span>
          <span>Exporter</span>
        </button>
      </div>
    </div>

    <!-- Pointage Table -->
    <div class="table-container" v-if="!pointageStore.loading">
      <table class="pointage-table">
        <thead>
          <tr>
            <th>Date/Heure</th>
            <th>Agent</th>
            <th>Matricule</th>
            <th>Direction</th>
            <th>Motif</th>
            <th>Durée</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody v-if="pointageStore.paginatedPointages.length > 0">
          <tr v-for="pointage in pointageStore.paginatedPointages" :key="pointage.id">
            <td>
              {{ formatDateTime(pointage.date) }}
            </td>
            <td>{{ pointage.agent.nom }} {{ pointage.agent.prenom }}</td>
            <td>
              <span class="matricule">{{ pointage.agent.matricule }}</span>
            </td>
            <td>
              <span class="direction-badge">{{ pointage.agent.direction }}</span>
            </td>
            <td>
              <span :class="`motif-badge ${getMotifClass(pointage.motif)}`">
                {{ pointage.motif }}
              </span>
            </td>
            <td>
              {{ pointage.duree ? formatDuration(pointage.duree) : 'N/A' }}
            </td>
            <td>
              <div class="action-buttons">
                <router-link :to="`/agent/${pointage.agent.id}?date=${pointage.date.split('T')[0]}`" class="view-btn">
                  <span class="material-icons">visibility</span>
                </router-link>
                <button class="edit-btn" @click="editPointage(pointage)">
                  <span class="material-icons">edit</span>
                </button>
                <button class="delete-btn" @click="confirmDelete(pointage)">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="7" class="no-data">
              <span class="material-icons">event_busy</span>
              <p>Aucun pointage trouvé</p>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Pagination -->
      <div class="pagination" v-if="pointageStore.filteredPointages.length > 0">
        <button 
          class="page-btn" 
          @click="goToPage(pointageStore.pagination.page - 1)" 
          :disabled="pointageStore.pagination.page === 1"
        >
          <span class="material-icons">navigate_before</span>
        </button>
        
        <span class="page-info">
          Page {{ pointageStore.pagination.page }} / {{ pointageStore.pagination.totalPages }}
        </span>
        
        <button 
          class="page-btn" 
          @click="goToPage(pointageStore.pagination.page + 1)" 
          :disabled="pointageStore.pagination.page === pointageStore.pagination.totalPages"
        >
          <span class="material-icons">navigate_next</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-container" v-if="pointageStore.loading">
      <div class="spinner"></div>
      <p>Chargement des données...</p>
    </div>

    <!-- Error Message -->
    <div class="error-message" v-if="pointageStore.error">
      <span class="material-icons">error_outline</span>
      <p>{{ pointageStore.error }}</p>
      <button @click="fetchPointages">
        <span class="material-icons">refresh</span>
        Réessayer
      </button>
    </div>

    <!-- Add/Edit Pointage Modal -->
    <!-- Ce composant sera implémenté plus tard -->
    <!-- <PointageFormModal
      v-if="showAddPointageModal || editingPointage"
      :pointage="editingPointage"
      :isEditing="!!editingPointage"
      @close="closeModal"
      @save="savePointage"
    /> -->

    <!-- Delete Confirmation Modal -->
    <!-- <DeleteConfirmModal
      v-if="pointageToDelete"
      :item="pointageToDelete"
      itemType="le pointage"
      @close="pointageToDelete = null"
      @confirm="deletePointage"
    /> -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { usePointageStore } from '@/store/pointage'
import { usePersonnelStore } from '@/store/personnel'
import Header from '@/components/Header.vue'
// import PointageFormModal from '@/components/modals/PointageFormModal.vue'
// import DeleteConfirmModal from '@/components/modals/DeleteConfirmModal.vue'

// Stores
const pointageStore = usePointageStore()
const personnelStore = usePersonnelStore()

// Component state
const searchQuery = ref('')
const selectedDate = ref(format(new Date(), 'yyyy-MM-dd'))
const selectedMotif = ref('Tous')
const selectedDirection = ref('Toutes')
const showAddPointageModal = ref(false)
const editingPointage = ref(null)
const pointageToDelete = ref(null)

// Computed properties
const motifs = computed(() => pointageStore.getMotifList)
const directions = computed(() => personnelStore.getDirections)

// Methods
const fetchPointages = async () => {
  if (!personnelStore.personnel.length) {
    await personnelStore.fetchPersonnel()
  }
  await pointageStore.fetchPointagesForDate(selectedDate.value)
}

const handleSearch = () => {
  pointageStore.setSearchQuery(searchQuery.value)
}

const handleDateChange = () => {
  pointageStore.setSelectedDate(selectedDate.value)
  fetchPointages()
}

const handleMotifChange = () => {
  pointageStore.setSelectedMotif(selectedMotif.value)
}

const handleDirectionChange = () => {
  pointageStore.setSelectedDirection(selectedDirection.value)
}

const goToPage = (page) => {
  if (page < 1 || page > pointageStore.pagination.totalPages) return
  pointageStore.setPage(page)
}

const formatDateTime = (dateTime) => {
  return format(parseISO(dateTime), 'dd MMM yyyy HH:mm', { locale: fr })
}

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h${mins.toString().padStart(2, '0')}`
}

const getMotifClass = (motif) => {
  const motifLower = motif.toLowerCase()
  if (motifLower.includes('présent')) return 'present'
  if (motifLower.includes('retard')) return 'late'
  if (motifLower.includes('absent') || motifLower.includes('congé') || motifLower.includes('permission')) return 'absent'
  return 'other'
}

const editPointage = (pointage) => {
  editingPointage.value = { ...pointage }
  showAddPointageModal.value = true
}

const confirmDelete = (pointage) => {
  pointageToDelete.value = pointage
}

const closeModal = () => {
  showAddPointageModal.value = false
  editingPointage.value = null
}

const savePointage = async (pointageData) => {
  // Cette fonction sera implémentée plus tard
  console.log('Sauvegarde du pointage:', pointageData)
  closeModal()
  await fetchPointages()
}

const deletePointage = async () => {
  // Cette fonction sera implémentée plus tard
  console.log('Suppression du pointage:', pointageToDelete.value)
  pointageToDelete.value = null
  await fetchPointages()
}

const exportToExcel = () => {
  // Cette fonction sera implémentée plus tard
  console.log('Export Excel des pointages')
}

// Lifecycle
onMounted(async () => {
  await fetchPointages()
  selectedMotif.value = 'Tous'
  selectedDirection.value = 'Toutes'
})
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.pointage-view {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-card .material-icons {
  font-size: 36px;
  margin-right: 16px;
}

.stat-card:nth-child(1) .material-icons {
  color: #1976d2;
}

.stat-card:nth-child(2) .material-icons {
  color: #4caf50;
}

.stat-card:nth-child(3) .material-icons {
  color: #f44336;
}

.stat-card:nth-child(4) .material-icons {
  color: #ff9800;
}

.stat-content h3 {
  font-size: 28px;
  margin: 0;
  line-height: 1;
  color: #333;
}

.stat-content p {
  margin: 5px 0 0;
  color: #666;
  font-size: 14px;
}

.control-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.filters {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.date-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-picker label, .filter-dropdown label {
  font-size: 14px;
  color: #666;
}

.date-picker input {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
}

.filter-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-dropdown select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  min-width: 120px;
}

.search-box {
  position: relative;
}

.search-box input {
  padding: 10px 16px 10px 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
  font-size: 14px;
}

.search-box .material-icons {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 20px;
}

.actions {
  display: flex;
  gap: 12px;
}

.add-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.add-btn {
  background-color: var(--turquoise-primary);
  color: white;
}

.export-btn {
  background-color: var(--turquoise-dark);
  color: white;
}

.add-btn:hover {
  background-color: var(--turquoise-dark);
}

.export-btn:hover {
  background-color: var(--turquoise-very-dark);
}

.export-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.pointage-table {
  width: 100%;
  border-collapse: collapse;
}

.pointage-table th {
  background-color: #f5f5f5;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #eee;
  position: sticky;
  top: 0;
}

.pointage-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  color: #444;
}

.pointage-table tr:last-child td {
  border-bottom: none;
}

.pointage-table tr:hover {
  background-color: #f9f9f9;
}

.matricule {
  font-family: monospace;
  background-color: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.direction-badge {
  display: inline-block;
  background-color: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  color: #333;
}

.motif-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  color: white;
}

.present {
  background-color: #4caf50;
}

.late {
  background-color: #ff9800;
}

.absent {
  background-color: #f44336;
}

.other {
  background-color: #9e9e9e;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.view-btn, .edit-btn, .delete-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.view-btn {
  background-color: #2196f3;
  color: white;
}

.edit-btn {
  background-color: #ff9800;
  color: white;
}

.delete-btn {
  background-color: #f44336;
  color: white;
}

.view-btn:hover {
  background-color: #1e88e5;
}

.edit-btn:hover {
  background-color: #f57c00;
}

.delete-btn:hover {
  background-color: #e53935;
}

.no-data {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.no-data .material-icons {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #eee;
  margin-top: auto;
}

.page-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.loading-container, .error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  flex-grow: 1;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid rgba(0, 0, 0, 0.1);
  border-left-color: #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-container p {
  color: #666;
  font-size: 16px;
}

.error-message .material-icons {
  font-size: 48px;
  color: #f44336;
  margin-bottom: 16px;
}

.error-message p {
  color: #666;
  font-size: 16px;
  margin-bottom: 24px;
}

.error-message button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  background-color: #1976d2;
  color: white;
  cursor: pointer;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 992px) {
  .control-panel {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters {
    flex-wrap: wrap;
  }
  
  .actions {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-picker, .filter-dropdown {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .date-picker input, .filter-dropdown select, .search-box input {
    width: 100%;
  }
  
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style>